/**
 * DriveZone — Reset Password Page
 * Route: /reset-password  (public)
 *
 * Three-step flow:
 *   Step 1 — Enter 6-digit reset code (same box UI as OTPPage)
 *   Step 2 — Enter new password + confirm
 *   Step 3 — Success → auto-redirect to /login
 *
 * Email is passed via React Router location state from ForgotPasswordPage.
 * If accessed directly, shows an email input at the top.
 */

import {
  useState, useRef, useEffect, useCallback,
  type KeyboardEvent, type ClipboardEvent, type FormEvent,
} from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { MapPin, Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react'
import api from '@/lib/api'
import { AxiosError } from 'axios'

const CODE_LENGTH  = 6
const EXPIRY_SECS  = 15 * 60  // 15 minutes

type Step = 'code' | 'password' | 'success'

export default function ResetPasswordPage() {
  const navigate      = useNavigate()
  const location      = useLocation()
  const emailFromState = (location.state as { email?: string; code?: string })?.email ?? ''
  const codeFromState  = (location.state as { email?: string; code?: string })?.code  ?? ''

  const [email, setEmail]           = useState(emailFromState)
  // If a verified code was passed from ForgotPasswordPage, skip straight to step 2
  const [step, setStep]             = useState<Step>(codeFromState ? 'password' : 'code')
  const [digits, setDigits]         = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const [verifiedCode, setVerifiedCode] = useState(codeFromState)
  const [timeLeft, setTimeLeft]     = useState(EXPIRY_SECS)
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState('')
  const [newPassword, setNewPassword]         = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNew, setShowNew]       = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // 15-minute countdown — runs only during code step
  useEffect(() => {
    if (step !== 'code') return
    const t = setInterval(() => setTimeLeft(s => Math.max(0, s - 1)), 1000)
    return () => clearInterval(t)
  }, [step])

  // Focus first box on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  function formatTime(secs: number) {
    const m = Math.floor(secs / 60).toString().padStart(2, '0')
    const s = (secs % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  // ── Step 1: verify code ────────────────────────────────────────────────
  const verifyCode = useCallback(async (code: string) => {
    if (!email) return
    setError('')
    setLoading(true)
    try {
      await api.post('/api/auth/verify-reset-code/', {
        email: email.trim().toLowerCase(),
        code,
      })
      setVerifiedCode(code)
      setStep('password')
    } catch (err) {
      const e = err as AxiosError<{ message?: string }>
      if (e.response?.status === 429) {
        setError('Too many attempts. Please wait before trying again.')
      } else {
        setError('Invalid or expired code. Please try again.')
      }
      setDigits(Array(CODE_LENGTH).fill(''))
      setTimeout(() => inputRefs.current[0]?.focus(), 0)
    } finally {
      setLoading(false)
    }
  }, [email])

  function handleChange(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1)
    const nd    = [...digits]
    nd[index]   = digit
    setDigits(nd)
    if (digit) {
      if (index < CODE_LENGTH - 1) inputRefs.current[index + 1]?.focus()
      if (nd.every(d => d !== '') && nd.join('').length === CODE_LENGTH) {
        verifyCode(nd.join(''))
      }
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const nd = [...digits]; nd[index] = ''; setDigits(nd)
      } else if (index > 0) {
        const nd = [...digits]; nd[index - 1] = ''; setDigits(nd)
        inputRefs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!pasted) return
    const nd = Array(CODE_LENGTH).fill('')
    for (let i = 0; i < pasted.length; i++) nd[i] = pasted[i]
    setDigits(nd)
    inputRefs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus()
    if (pasted.length === CODE_LENGTH) verifyCode(pasted)
  }

  // ── Step 2: reset password ─────────────────────────────────────────────
  async function handleReset(e: FormEvent) {
    e.preventDefault()
    setError('')

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.'); return
    }
    if (!/\d/.test(newPassword)) {
      setError('Password must contain at least one number.'); return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.'); return
    }

    setLoading(true)
    try {
      await api.post('/api/auth/confirm-reset/', {
        email:            email.trim().toLowerCase(),
        code:             verifiedCode,
        new_password:     newPassword,
        confirm_password: confirmPassword,
      })
      setStep('success')
      setTimeout(() => navigate('/login', {
        state: { message: 'Password reset successfully. Please sign in with your new password.' },
      }), 3000)
    } catch (err) {
      const e = err as AxiosError<{ message?: string }>
      setError(e.response?.data?.message ?? 'Could not reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 3: success ────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm card p-8 text-center space-y-5">
          <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">Password reset!</h1>
            <p className="text-sm text-slate-500 mt-2">
              You can now sign in with your new password.
            </p>
          </div>
          <button
            onClick={() => navigate('/login', {
              state: { message: 'Password reset successfully. Please sign in with your new password.' },
            })}
            className="btn-primary w-full"
          >
            Sign in
          </button>
          <p className="text-xs text-slate-400">Redirecting in 3 seconds…</p>
        </div>
      </div>
    )
  }

  // ── Step 2: new password form ──────────────────────────────────────────
  if (step === 'password') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-600 mb-4">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Create new password</h1>
            <p className="text-sm text-slate-500 mt-1">At least 8 characters and 1 number</p>
          </div>

          <div className="card p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  New password
                </label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    className="input pr-10"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    autoFocus
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Confirm new password
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className="input pr-10"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="btn-primary w-full"
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Resetting…</>
                  : 'Reset password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // ── Step 1: enter code ─────────────────────────────────────────────────
  const currentCode = digits.join('')

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-600 mb-4">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Enter reset code</h1>
          {email ? (
            <p className="text-sm text-slate-500 mt-1">
              We sent a 6-digit code to{' '}
              <span className="font-medium text-slate-700">{email}</span>
            </p>
          ) : (
            <p className="text-sm text-slate-500 mt-1">Enter the 6-digit code from your email</p>
          )}
        </div>

        <div className="card p-6 space-y-6">
          {/* Show email input if accessed directly (no state) */}
          {!emailFromState && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Email address
              </label>
              <input
                type="email"
                className="input"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-600 text-center">{error}</p>
            </div>
          )}

          {/* 6-digit boxes */}
          <div className="flex gap-2 justify-center">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={el => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={e => e.target.select()}
                disabled={loading}
                className={`
                  w-11 h-14 text-center text-xl font-bold rounded-lg border-2
                  transition-all duration-150
                  focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${digit
                    ? 'border-primary-400 bg-primary-50 text-primary-700'
                    : 'border-slate-200 bg-white text-slate-900'
                  }
                `}
                aria-label={`Digit ${index + 1}`}
              />
            ))}
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
            </div>
          )}

          {!loading && currentCode.length > 0 && currentCode.length < CODE_LENGTH && (
            <p className="text-center text-xs text-slate-400">
              {CODE_LENGTH - currentCode.length} digit{CODE_LENGTH - currentCode.length !== 1 ? 's' : ''} remaining
            </p>
          )}

          <p className="text-center text-xs text-slate-400">
            Code expires in{' '}
            <span className={`font-mono font-semibold ${timeLeft < 60 ? 'text-red-500' : 'text-slate-600'}`}>
              {formatTime(timeLeft)}
            </span>
          </p>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <Link to="/forgot-password" className="text-primary-600 font-medium hover:underline">
            ← Request a new code
          </Link>
        </p>
      </div>
    </div>
  )
}
